import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdAuthorityKey } from '../basic-properties/hd-chart-properties.types';
import { HD_AUTHORITY_INFO_FILES, HD_AUTHORITY_INFO_ORDER } from './hd-chart-basics-info-metadata';
import { HdChartBasicsInfoPanelMode, hdOpenAllInfoPanel } from './hd-chart-basics-info-panels.util';
import { HdChartBasicsInfoService } from './hd-chart-basics-info.service';
import { HdInfoSectionsComponent } from './hd-info-sections.component';
import { HdInfoSectionVm, HdInfoSectionsVm } from './hd-info-sections.models';

/** Paneel-data: de autoriteit van de actieve chart voedt de "jouw autoriteit"-markering. */
export interface HdAuthorityInfoPanelData {
  readonly authorityKey: HdAuthorityKey | null;
  readonly mode?: HdChartBasicsInfoPanelMode;
}

/**
 * Info-paneel "Autoriteit": algemeen document (hiërarchie, vergelijking) en
 * de eigen autoriteit; "Toon alle 7 autoriteiten" opent het overzichtspaneel
 * (modus `all`): alle zeven in hiërarchische volgorde als inklapbare lijst.
 */
@Component({
  selector: 'hd-authority-info-panel',
  standalone: true,
  imports: [HdInfoSectionsComponent],
  template: '<hd-info-sections [vm]="vm()" (showAllRequested)="openAll()" />',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdAuthorityInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdAuthorityInfoPanelData | null> | null;
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly info = inject(HdChartBasicsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly ownKey = computed<HdAuthorityKey | null>(() => this.panelData?.()?.authorityKey ?? null);
  private readonly mode = computed<HdChartBasicsInfoPanelMode>(() => this.panelData?.()?.mode ?? 'own');

  protected readonly vm = computed<HdInfoSectionsVm>(() => {
    const lang = this.lang();
    const t = (key: string) => this.info.text(`authority.${key}`, lang);
    const ownKey = this.ownKey();
    const sections: HdInfoSectionVm[] = HD_AUTHORITY_INFO_ORDER.map(key => ({
      key,
      label: this.info.propertyText(`authorityValues.${key}`, lang),
      subtitle: t(`subtitle.${key}`),
      url: this.info.markdownUrl('authorities', HD_AUTHORITY_INFO_FILES[key], lang),
      isOwn: key === ownKey,
      badges: key === ownKey ? [t('ownBadgeLabel')] : [],
      facts: [
        { label: t('facts.center'), value: t(`center.${key}`) },
        { label: t('facts.signal'), value: t(`signal.${key}`) },
        { label: t('facts.timing'), value: t(`timing.${key}`) },
      ],
    }));
    if (this.mode() === 'all') {
      return { title: t('allTitle'), sections, collapsible: true };
    }
    const own = sections.find(section => section.isOwn);
    return {
      title: t('title'),
      jumpToStripLabel: own ? t('jumpToOwnLabel') : undefined,
      generalUrl: this.info.markdownUrl('authorities', 'general', lang),
      strip: own
        ? { label: t('stripLabel'), showAllLabel: t('showAllLabel'), chips: [{ sectionKey: own.key, label: own.label }] }
        : undefined,
      sections,
    };
  });

  protected openAll(): void {
    hdOpenAllInfoPanel<HdAuthorityInfoPanelComponent, HdAuthorityInfoPanelData>(this.floatingPanels, this.panelRef, {
      key: 'authority-info-all',
      component: HdAuthorityInfoPanelComponent,
      data: { authorityKey: this.ownKey(), mode: 'all' },
      tabLabel: this.info.text('authority.allTabLabel', this.lang()),
    });
  }
}
